import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const stages = [
  { label: '课程列表', icon: 'ri-file-list-3-line', color: 'primary' as const },
  { label: 'AI定位关键片段', icon: 'ri-scan-line', color: 'accent' as const },
  { label: '查看证据', icon: 'ri-file-search-line', color: 'primary' as const },
  { label: '专家判断', icon: 'ri-user-star-line', color: 'accent' as const },
  { label: '意见沉淀', icon: 'ri-archive-line', color: 'primary' as const },
];

const categories = [
  { label: '导入与问题设计', icon: 'ri-guide-line' },
  { label: '理论阐释', icon: 'ri-lightbulb-line' },
  { label: '案例使用', icon: 'ri-stack-line' },
  { label: '互动回应', icon: 'ri-chat-3-line' },
  { label: '总结升华', icon: 'ri-flag-line' },
];

export default function P64ServiceManager({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);

  return (
    <div className="relative h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      <div ref={orb1Ref} className="absolute top-20 right-24 w-80 h-80 rounded-full bg-primary-100/10 blur-[100px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-28 left-12 w-96 h-96 rounded-full bg-accent-100/8 blur-[120px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-6 md:py-8">
        <div ref={titleRef} className="text-center mb-4 md:mb-5">
          <span className={`inline-flex items-center gap-2 text-xs font-body font-semibold text-gold-600 uppercase tracking-[0.15em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <span className="gold-dot" />服务专家
          </span>
          <h2 className={`font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-4 transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            从完整拖视频到<span className="text-gold-600">证据化网评</span>
          </h2>
          <p className={`text-sm text-foreground-600 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            AI先帮助专家找到证据，专家再对证据作出专业判断
          </p>
        </div>

        {/* Expert Workflow */}
        <div className={`max-w-5xl mx-auto mb-4 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          <div className="flex flex-wrap items-center justify-center gap-2 md:gap-3 mb-4">
            {stages.map((s, i) => (
              <div key={s.label} className="flex items-center gap-2" style={{ transitionDelay: `${300 + i * 100}ms` }}>
                <div className="px-4 py-2.5 rounded-xl glass-liquid flex items-center gap-2">
                  <div className={`w-7 h-7 rounded-md ${s.color === 'accent' ? 'bg-gold-100/80 border border-gold-300/40' : 'bg-primary-100/60 border border-primary-200/40'} flex items-center justify-center`}>
                    <i className={`${s.icon} text-xs ${s.color === 'accent' ? 'text-gold-600' : 'text-primary-600'}`} />
                  </div>
                  <span className="text-xs font-body font-semibold text-foreground-800 whitespace-nowrap">{s.label}</span>
                </div>
                {i < stages.length - 1 && (
                  <i className="ri-arrow-right-line text-sm text-foreground-300 hidden md:block" />
                )}
              </div>
            ))}
          </div>

          {/* Categories */}
          <div className="p-5 glass-liquid rounded-xl">
            <div className="relative z-10">
              <p className="text-xs font-body font-bold text-foreground-500 mb-3 text-center">系统预识别关键片段分类</p>
              <div className="flex flex-wrap justify-center gap-2 md:gap-3">
                {categories.map((c, i) => (
                  <div
                    key={c.label}
                    className="flex items-center gap-2 px-3 py-2 rounded-lg bg-primary-100/60 border border-primary-200/40"
                    style={{ transitionDelay: `${500 + i * 80}ms` }}
                  >
                    <i className={`${c.icon} text-xs text-primary-600`} />
                    <span className="text-xs font-body font-semibold text-foreground-800 whitespace-nowrap">{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 delay-700 ${titleVisible ? 'opacity-100' : 'opacity-0'}`}>
          <div className="p-5 glass-liquid rounded-xl">
            <p className="text-sm text-foreground-600 font-body leading-relaxed">
              专家直接进入相关证据，把主要精力放在专业判断上。当不同专家观点不一致时，可以回到同一段课堂原话讨论——争议从<strong className="text-gold-700">"印象不同"转变为"对同一证据的专业解释不同"</strong>。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}